import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Search, Plus, FileText, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import StatCard from '../../components/data/StatCard';
import { invoicesApi } from '../../../lib/api';
import { useAppStore } from '../../../lib/store';
import { formatDate } from '../../../lib/dateUtils';

interface Invoice {
  id: string;
  invoice_number: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  amount: number;
  vat_amount: number;
  total_amount: number;
  issue_date: string;
  due_date: string;
  description?: string;
  property?: {
    title?: string;
    address?: string;
    property_code?: string;
  };
  tenant?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
  };
}

export default function InvoicesList() {
  const { t } = useTranslation(['finance', 'common']);
  const navigate = useNavigate();
  const user = useAppStore((state) => state.user);
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      // Managers and admins see all invoices, no filters needed
      // Other users would need property/tenant filters, but for now we show all
      const filters: any = {};
      if (filterStatus !== 'all') filters.status = filterStatus;

      const response = await invoicesApi.getAll(filters);
      
      if (response.error) {
        throw new Error(response.error.message || 'Failed to load invoices');
      }
      
      if (response.data) {
        let filtered = response.data;
        
        // Client-side search filtering
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          filtered = filtered.filter((inv: Invoice) => {
            const invoiceNumber = inv.invoice_number?.toLowerCase() || '';
            const description = inv.description?.toLowerCase() || '';
            const propertyName = inv.property?.title?.toLowerCase() || inv.property?.address?.toLowerCase() || '';
            const tenantName = `${inv.tenant?.first_name || ''} ${inv.tenant?.last_name || ''}`.toLowerCase().trim();
            
            return invoiceNumber.includes(query) ||
                   description.includes(query) ||
                   propertyName.includes(query) ||
                   tenantName.includes(query);
          });
        }
        
        setInvoices(filtered);
      } else {
        setInvoices([]);
      }
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [filterStatus, searchQuery]);

  const stats = {
    total: invoices.length,
    sent: invoices.filter((inv) => inv.status === 'sent').length,
    paid: invoices.filter((inv) => inv.status === 'paid').length,
    overdue: invoices.filter((inv) => inv.status === 'overdue').length,
  };

  const statusColors = {
    draft: 'default',
    sent: 'info',
    paid: 'success',
    overdue: 'error',
    cancelled: 'default',
  } as const;

  const statusIcons = {
    draft: <FileText size={20} />,
    sent: <Clock size={20} />,
    paid: <CheckCircle size={20} />,
    overdue: <AlertCircle size={20} />,
    cancelled: <FileText size={20} />,
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchInvoices}>
          {t('common:retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('invoices')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('subtitle')}
        </Typography>
      </Box>

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('totalTransactions')}
            value={stats.total}
            icon={<FileText />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('invoiceStatuses.sent')}
            value={stats.sent}
            icon={<Clock />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('invoiceStatuses.paid')}
            value={stats.paid}
            icon={<CheckCircle />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('overdueInvoices')}
            value={stats.overdue}
            icon={<AlertCircle />}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('filterByStatus')}</InputLabel>
                <Select
                  value={filterStatus}
                  label={t('filterByStatus')}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">{t('common:all')}</MenuItem>
                  <MenuItem value="draft">{t('invoiceStatuses.draft')}</MenuItem>
                  <MenuItem value="sent">{t('invoiceStatuses.sent')}</MenuItem>
                  <MenuItem value="paid">{t('invoiceStatuses.paid')}</MenuItem>
                  <MenuItem value="overdue">{t('invoiceStatuses.overdue')}</MenuItem>
                  <MenuItem value="cancelled">{t('invoiceStatuses.cancelled')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Plus size={20} />}
                sx={{ height: '56px' }}
                onClick={() => navigate('/dashboard/finance/invoices/create')}
              >
                {t('addInvoice')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invoices List */}
      <Grid container spacing={2}>
        {invoices.map((invoice) => (
          <Grid item xs={12} key={invoice.id}>
            <Card 
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
              onClick={() => navigate(`/dashboard/finance/invoices/${invoice.id}`)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {statusIcons[invoice.status]}
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {invoice.invoice_number}
                    </Typography>
                  </Box>
                  <Chip
                    label={t(`invoiceStatuses.${invoice.status}`)}
                    color={statusColors[invoice.status]}
                    size="small"
                  />
                </Box>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  {invoice.description || '-'}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('tenant')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {invoice.tenant 
                        ? `${invoice.tenant.first_name || ''} ${invoice.tenant.last_name || ''}`.trim() || '-'
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('property')}
                    </Typography>
                    <Typography variant="body2">
                      {invoice.property 
                        ? invoice.property.title || invoice.property.address || invoice.property.property_code || '-'
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">
                      {t('amount')}
                    </Typography>
                    <Typography variant="body2">
                      {Number(invoice.amount).toLocaleString()} {t('common:currency')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">
                      {t('vatAmount')}
                    </Typography>
                    <Typography variant="body2">
                      {Number(invoice.vat_amount || 0).toLocaleString()} {t('common:currency')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">
                      {t('totalAmount')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {Number(invoice.total_amount).toLocaleString()} {t('common:currency')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('issueDate')}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(invoice.issue_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('dueDate')}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(invoice.due_date)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {invoices.length === 0 && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('noTransactionsFound')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('adjustSearch')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
